import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { STORAGE_KEYS } from '../../lib/config'

interface AuthState {
  accessToken: string | null
  user: {
    id: string
    email: string
    displayName: string
    avatarUrl: string | null
    roles: string[]
  } | null
  isAuthenticated: boolean
  isLoading: boolean // true while checking session on app load
  isRetrying: boolean // true while retrying after BE restart
}

const initialState: AuthState = (() => {
  // Restore auth state from sessionStorage on page load / hot reload
  // sessionStorage survives hot reloads but clears when tab is closed
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS.auth)
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<AuthState>
      if (parsed.accessToken && parsed.user) {
        return {
          accessToken: parsed.accessToken,
          user: parsed.user,
          isAuthenticated: true,
          isLoading: false, // already have a token, no need to show loading
          isRetrying: false,
        }
      }
    }
  } catch {}
  return {
    accessToken: null,
    user: null,
    isAuthenticated: false,
    isLoading: true,
    isRetrying: false,
  }
})()

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload
      state.isAuthenticated = true
      state.isLoading = false
      // Don't persist to sessionStorage here — user may not be set yet.
      // setUser (called right after) handles the persistence with the full user object.
    },
    setUser: (
      state,
      action: PayloadAction<AuthState['user']>,
    ) => {
      state.user = action.payload
      // Keep sessionStorage in sync
      try {
        if (state.accessToken) {
          sessionStorage.setItem(STORAGE_KEYS.auth, JSON.stringify({
            accessToken: state.accessToken,
            user: action.payload,
          }))
        }
      } catch {}
    },
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setRetrying: (state, action: PayloadAction<boolean>) => {
      state.isRetrying = action.payload
    },
    logout: (state) => {
      state.accessToken = null
      state.user = null
      state.isAuthenticated = false
      state.isLoading = false
      // Clear persisted session
      try { sessionStorage.removeItem(STORAGE_KEYS.auth) } catch {}
      try { localStorage.removeItem(STORAGE_KEYS.refreshToken) } catch {}
    },
  },
})

export const { setAccessToken, setUser, setAuthLoading, setRetrying, logout } =
  authSlice.actions
export default authSlice.reducer
